
import { ControlGroup } from "../controls/ControlGroup";
import { Control } from "../controls/Control";

/**
 * Core class representing a toolbar UI.
 *
 * It consists of {@link Autodesk.Viewing.UI.ControlGroup} that group controls by functionality.
 *
 * @alias Autodesk.Viewing.UI.ToolBar
 * @param {string} id - The id for this toolbar.
 * @param {object} [options] - An optional dictionary of options.
 * @param {boolean} [options.collapsible=true] - Whether this toolbar is collapsible.
 * @param {boolean} [options.alignVertically=false] - Whether this toolbar should be vertically positioned on the right side.
 * @class
 * @augments Autodesk.Viewing.UI.ControlGroup
 * @memberof Autodesk.Viewing.UI
 */
export function ToolBar(id, options) {
  ControlGroup.call(this, id, options);

  this.removeClass('adsk-control-group');
  this.addClass('adsk-toolbar');

  if (options && options.alignVertically) {
    this.addClass('adsk-toolbar-vertical');
  }

  this.isVertical = function () {
    return this.container.classList.contains("adsk-toolbar-vertical");
  };

  // returns the amount vertical toolbar is shifted from right excluding its default right offset (e.g. using shiftControls method)
  this.getRightShift = function () {
    if (!this.isVertical() || !parseInt(this.container.dataset.right, 10)) {
      return 0;
    }

    return parseInt(this.container.dataset.right, 10);
  };
}

/**
 * Enum for toolbar event IDs.
 *
 * @readonly
 * @enum {string}
 */
ToolBar.Event = {
  // Inherited from Control
  VISIBILITY_CHANGED: Control.Event.VISIBILITY_CHANGED,
  COLLAPSED_CHANGED: Control.Event.COLLAPSED_CHANGED,

  // Inherited from ControlGroup
  CONTROL_ADDED: ControlGroup.Event.CONTROL_ADDED,
  CONTROL_REMOVED: ControlGroup.Event.CONTROL_REMOVED,
  SIZE_CHANGED: ControlGroup.Event.SIZE_CHANGED
};

ToolBar.prototype = Object.create(ControlGroup.prototype);
ToolBar.prototype.constructor = ToolBar;

/**
 * Changes the position of toolbar and related elements
 *
 * @param {number|null|undefined} right - the amount of right offset, use null to reset or undefined to keep it unchanged
 * @param {number|null|undefined} top - the amount of top offset, use null to reset or undefined to keep it unchanged
 */
ToolBar.shiftControls = function (right, top) {
  const toolbarSelector = '.adsk-viewing-viewer .adsk-toolbar.adsk-toolbar-vertical';
  const cubeSelector = '.viewcubeWrapper';

  for (const element of document.querySelectorAll(toolbarSelector)) {
    translate(element, right, top);
  }

  for (const element of document.querySelectorAll(cubeSelector)) {
    translate(element, right, top);
  }

  const bimWalkInfoButton = document.querySelector('#tooltip-info');
  if (bimWalkInfoButton) {
    translate(bimWalkInfoButton, right, top);
  }
};

const translate = (element, right, top) => {
  if (element.dataset.right === undefined) element.dataset.right = 0;
  if (element.dataset.top === undefined) element.dataset.top = 0;

  if (right !== undefined) element.dataset.right = right || 0;
  if (top !== undefined) element.dataset.top = top || 0;

  element.style.translate = `-${element.dataset.right}px ${element.dataset.top}px`;
};